import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import cx from "./Waitlist.module.scss";
import { Logo } from "../../../assets/images";
import { FaInstagram, FaTaxi } from "react-icons/fa";
import { FiCheck, FiHelpCircle } from "react-icons/fi";
import { usePageMeta } from "../../../hooks/usePageMeta";

const MIN_FILL_MS = 1500;
const MAX_NAME_LEN = 50;
const MAX_CITY_LEN = 100;

const sanitizeName = (value: string) =>
  value.replace(/[<>()]/g, "").replace(/\s+/g, " ").slice(0, MAX_NAME_LEN).trimStart();

const isValidEmail = (value: string) =>
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value);

// CloudFront signs requests to the Lambda origins behind /api/* with SigV4
// (origin access control) — for POST/PUT, Lambda requires the caller to
// supply the request body's SHA-256 hash in x-amz-content-sha256, since it
// doesn't support unsigned payloads. Without this header the origin
// rejects every request with InvalidSignatureException regardless of the
// body's actual content.
const sha256Hex = async (text: string): Promise<string> => {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const postJson = async (url: string, body: Record<string, unknown>) => {
  const raw = JSON.stringify(body);
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-amz-content-sha256": await sha256Hex(raw),
    },
    body: raw,
  });
};

type TurnstileProps = {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
};

const Turnstile = (props: TurnstileProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const cbRef = useRef(props);
  cbRef.current = props;

  useEffect(() => {
    const win = window as any;
    if (!containerRef.current) return;
    if (!win.turnstile) return;
    if (widgetIdRef.current !== null) return;
    widgetIdRef.current = win.turnstile.render(containerRef.current, {
      sitekey: cbRef.current.siteKey,
      theme: "dark",
      callback: (token: string) => cbRef.current.onToken(token),
      "expired-callback": () => cbRef.current.onExpire?.(),
      "error-callback": () => cbRef.current.onError?.(),
    });
    return () => {
      if (widgetIdRef.current && (window as any).turnstile) {
        try {
          (window as any).turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  return <div ref={containerRef} className={cx.turnstileBox} />;
};

const loadTurnstileScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const win = window as any;
    if (win.turnstile) {
      resolve();
      return;
    }
    const existing = document.querySelector(
      'script[src*="challenges.cloudflare.com"]'
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Turnstile"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () =>
      reject(new Error("Failed to load Turnstile"))
    );
    document.head.appendChild(script);
  });
};

const loadPlacesScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const win = window as any;
    if (win.google?.maps?.places) {
      resolve();
      return;
    }
    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";
    if (!key) {
      reject(new Error("Missing Google Maps key"));
      return;
    }
    const existing = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Google Places"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&language=en`;
    script.async = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () =>
      reject(new Error("Failed to load Google Places"))
    );
    document.head.appendChild(script);
  });
};

const Waitlist = () => {
  usePageMeta(
    "TaxiMo - Launching Soon",
    "TaxiMo is launching soon. Join the waitlist to get early access when we go live."
  );

  const [firstName, setFirstName] = useState("");
  const [city, setCity] = useState("");
  const [citySelected, setCitySelected] = useState(false);
  const [email, setEmail] = useState("");
  const [honeyName, setHoneyName] = useState("");
  const [honeyEmail, setHoneyEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "exists"
  >("idle");
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const [myReferralCode, setMyReferralCode] = useState("");
  const [foundingMember, setFoundingMember] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [cookieNoticeDismissed, setCookieNoticeDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("cookieNoticeDismissed") === "1"
  );
  const dismissCookieNotice = useCallback(() => {
    setCookieNoticeDismissed(true);
    try {
      localStorage.setItem("cookieNoticeDismissed", "1");
    } catch {
      // Storage can be unavailable (private mode, blocked) — the notice
      // just re-shows next visit, which is a harmless fallback.
    }
  }, []);
  const [referralCode, setReferralCode] = useState(() =>
    new URLSearchParams(window.location.search).get("ref")?.trim().toUpperCase() || ""
  );
  const [referredViaLink] = useState(() => Boolean(referralCode));
  const [mountedAt] = useState(() => Date.now());
  const [turnstileSiteKey] = useState(
    () => process.env.REACT_APP_TURNSTILE_SITE_KEY || ""
  );
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const cityRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  useEffect(() => {
    let mounted = true;
    fetch("/api/join-waitlist")
      .then((response) => response.json())
      .then((data) => {
        if (mounted && typeof data.count === "number") setCount(data.count);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey) return;
    loadTurnstileScript()
      .then(() => setTurnstileReady(true))
      .catch(() => setError("Verification is unavailable right now. Please refresh and try again."));
  }, [turnstileSiteKey]);

  const handleToken = useCallback((token: string) => {
    setTurnstileToken(token);
    setError("");
  }, []);

  const handleExpire = useCallback(() => setTurnstileToken(""), []);
  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setError("Verification failed. Please complete the check again.");
  }, []);

  const initCityAutocomplete = useCallback(() => {
    const win = window as any;
    if (!cityRef.current || autocompleteRef.current) return;
    if (!win.google?.maps?.places) return;
    autocompleteRef.current = new win.google.maps.places.Autocomplete(
      cityRef.current,
      { types: ["(cities)"] }
    );
    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current?.getPlace();
      if (place && place.formatted_address) {
        setCity(place.formatted_address);
        setCitySelected(true);
        setError("");
      }
    });
  }, []);

  const handleCityFocus = useCallback(() => {
    loadPlacesScript()
      .then(initCityAutocomplete)
      .catch(() => {});
  }, [initCityAutocomplete]);

  const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCity(event.target.value);
    setCitySelected(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Honeypots: hidden fields must stay empty (bots fill them).
    if (honeyName || honeyEmail) {
      setStatus("success");
      return;
    }

    // Timing trap: genuine users take at least a couple of seconds to fill.
    if (Date.now() - mountedAt < MIN_FILL_MS) {
      setError("Form submitted too quickly. Please try again.");
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    const cleanName = firstName.trim().replace(/\s+/g, " ").slice(0, MAX_NAME_LEN);
    const cleanCity = city.trim().slice(0, MAX_CITY_LEN);

    if (!isValidEmail(cleanEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!cleanCity) {
      setError("Please enter your city.");
      return;
    }
    if (!citySelected) {
      setError("Please select your city from the suggestions.");
      return;
    }
    if (!turnstileSiteKey) {
      setError(
        "Verification is not configured yet. Please try again shortly."
      );
      return;
    }
    if (!turnstileToken) {
      setError("Please complete the human verification.");
      return;
    }

    setError("");
    setStatus("submitting");
    try {
      // The actual write happens server-side (lambda/join-waitlist): it
      // verifies the Turnstile token against Cloudflare before creating the
      // waitlist item, since the client has no direct read/write access to
      // the DynamoDB table and couldn't make that outbound call itself.
      const response = await postJson("/api/join-waitlist", {
        email: cleanEmail,
        firstName: cleanName,
        city: cleanCity,
        turnstileToken,
        referralCode: referralCode.trim().toUpperCase() || undefined,
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setStatus("idle");
        setError(
          result.error || "Something went wrong. Please try again in a moment."
        );
        return;
      }

      if (result.referralCode) setMyReferralCode(result.referralCode);
      setFoundingMember(Boolean(result.foundingMember));

      if (result.status === "exists") {
        setStatus("exists");
        return;
      }

      if (count > 0) setCount((current) => current + 1);
      setStatus("success");
      // Best-effort: the join-waitlist call above is what actually reserves
      // the spot, so a failure here shouldn't affect the success state shown
      // to the user.
      postJson("/api/send-waitlist-email", { email: cleanEmail }).catch(() => {});
    } catch (err) {
      console.error("Waitlist signup failed:", err);
      setStatus("idle");
      setError("Something went wrong. Please try again in a moment.");
    }
  };

  const done = status === "success" || status === "exists";
  const referralLink = myReferralCode
    ? `${window.location.origin}/?ref=${myReferralCode}`
    : "";

  const handleCopyReferralLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard API can be unavailable/denied — the code and link are
      // still shown as plain text, so the user can select and copy manually.
    }
  }, [referralLink]);

  return (
    <div className={cx.waitlistPage}>
      <main className={cx.mainArea}>
        <section className={cx.card}>
          <div className={cx.cardTop}>
            <img className={cx.logo} src={Logo} alt="TaxiMo" />
          </div>
          <span className={cx.pill}>Waitlist</span>

          <h1 className={cx.title}>
            <span className={cx.titleTop}>TaxiMo is</span>
            <span className={cx.titleBottom}>launching soon</span>
          </h1>

          <p className={cx.offer}>
            First 100 signups get <strong>15% off rides for 3 months</strong>
          </p>

          <p className={cx.description}>
            TaxiMo lets you order a taxi straight from your phone, no more
            waiting on hold or hunting for a dispatch number. Enter your
            pickup and drop-off, and a local driver comes to you.
          </p>

          <p className={cx.subtitle}>
            Join the waitlist to get early access when we go live.
          </p>

          {done ? (
            <>
              <div className={cx.successBox} role="status">
                <span className={cx.successIcon}>
                  <FiCheck />
                </span>
                <span>
                  {status === "exists"
                    ? "You're already on the list — we'll be in touch soon."
                    : foundingMember
                    ? "You're on the list, and one of our first 100 — your 15% off for 3 months is locked in!"
                    : "You're on the list! We'll email you when we go live."}
                </span>
              </div>

              {myReferralCode && (
                <div className={cx.referralBox}>
                  <p className={cx.referralTitle}>Invite friends, earn coupons</p>
                  <p className={cx.referralCopy}>
                    Share your link. When a friend joins using it, you both get
                    a 15% off coupon added to your account, up to 10 coupons
                    each.
                  </p>
                  <div className={cx.referralLinkRow}>
                    <input
                      type="text"
                      className={cx.referralLinkInput}
                      value={referralLink}
                      readOnly
                      aria-label="Your referral link"
                      onFocus={(event) => event.target.select()}
                    />
                    <button
                      type="button"
                      className={cx.referralCopyBtn}
                      onClick={handleCopyReferralLink}
                    >
                      {linkCopied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <form className={cx.form} onSubmit={handleSubmit} noValidate>
              {referredViaLink && (
                <p className={cx.referredNote}>You were invited by a friend!</p>
              )}
              <div className={cx.field}>
                <input
                  type="text"
                  className={cx.input}
                  placeholder="First name (optional)"
                  aria-label="First name (optional)"
                  maxLength={MAX_NAME_LEN}
                  value={firstName}
                  onChange={(event) => setFirstName(sanitizeName(event.target.value))}
                  autoComplete="given-name"
                  disabled={status === "submitting"}
                />
                <span className={cx.hintWrap} tabIndex={0}>
                  <FiHelpCircle className={cx.hintIcon} />
                  <span className={cx.tooltip} role="tooltip">
                    We only use your first name to greet you personally in
                    emails — nothing else.
                  </span>
                </span>
              </div>

              <div className={cx.field}>
                <input
                  ref={cityRef}
                  type="text"
                  className={cx.input}
                  placeholder="Your city"
                  aria-label="Your city"
                  maxLength={MAX_CITY_LEN}
                  value={city}
                  onChange={handleCityChange}
                  onFocus={handleCityFocus}
                  autoComplete="address-level2"
                  disabled={status === "submitting"}
                  required
                />
                {city && citySelected && (
                  <FiCheck className={cx.cityCheck} aria-hidden="true" />
                )}
              </div>

              <div className={cx.field}>
                <input
                  type="email"
                  className={cx.input}
                  placeholder="john@gmail.com"
                  aria-label="Email address"
                  maxLength={254}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  disabled={status === "submitting"}
                  required
                />
              </div>

              <div className={cx.field}>
                <input
                  type="text"
                  className={cx.input}
                  placeholder="Referral code (optional)"
                  aria-label="Referral code (optional)"
                  maxLength={8}
                  value={referralCode}
                  onChange={(event) => setReferralCode(event.target.value.toUpperCase())}
                  autoComplete="off"
                  disabled={status === "submitting"}
                />
              </div>

              {/* Honeypots — hidden from humans, irresistible to bots */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeyName}
                onChange={(event) => setHoneyName(event.target.value)}
                className={cx.honey}
                aria-hidden="true"
              />
              <input
                type="email"
                name="confirm_email"
                tabIndex={-1}
                autoComplete="off"
                value={honeyEmail}
                onChange={(event) => setHoneyEmail(event.target.value)}
                className={cx.honey}
                aria-hidden="true"
              />

              {turnstileSiteKey && turnstileReady && (
                <div className={cx.turnstileWrap}>
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onToken={handleToken}
                    onExpire={handleExpire}
                    onError={handleTurnstileError}
                  />
                </div>
              )}

              <button
                type="submit"
                className={cx.submit}
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Joining..." : "Subscribe"}
              </button>

              <p className={cx.consent}>
                By joining, you agree to our{" "}
                <Link to="/privacy-policy">Privacy Policy</Link> and{" "}
                <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
              </p>
            </form>
          )}

          {error && (
            <p className={cx.error} role="alert">
              {error}
            </p>
          )}

          {count > 0 && (
            <p className={cx.counter}>
              <FaTaxi className={cx.counterIcon} />
              Join {count}+ others.
            </p>
          )}
        </section>
      </main>

      <div className={cx.socialPill}>
        <a
          href="https://instagram.com/taximoapp"
          target="_blank"
          rel="noreferrer"
          aria-label="TaxiMo on Instagram"
        >
          <FaInstagram />
        </a>
      </div>

      <footer className={cx.footer}>
        <p>Copyright {new Date().getFullYear()} TaxiMo. All Rights Reserved.</p>
        <p className={cx.footerLinks}>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span aria-hidden="true"> · </span>
          <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
        </p>
      </footer>

      {!cookieNoticeDismissed && (
        <div className={cx.cookieNotice} role="status">
          <p>
            We use essential cookies for bot protection and address
            autocomplete. See our{" "}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>
          <button
            type="button"
            className={cx.cookieNoticeBtn}
            onClick={dismissCookieNotice}
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
};

export default Waitlist;